import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import setFilterQuery from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetFilterQuery";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tIronValleySupply/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    filterApplied?: boolean;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const ShowSelectedFilter = state.IronValleyData.ShowSelectedFilterStore.ShowSelectedFilter;
    const isMultipleFilterSelected = state.IronValleyData.MultipleFilterStore.isMultipleFilterSelected;
    const ButtonClicked = state.IronValleyData.ShowSelectedFilterStore.buttonClicked;
    const location = getLocation(state);
    const pageType = getCurrentPage(state);
    const productListFilterDetails = state.pages.productList;

    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;
        const mergeAttributeTypeFacets = (
            unfilteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
            filteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
        ) => {
            if (!unfilteredAttributeTypeFacets) {
                return filteredAttributeTypeFacets;
            }

            if (!filteredAttributeTypeFacets) {
                return unfilteredAttributeTypeFacets;
            }

            return unfilteredAttributeTypeFacets.concat(
                filteredAttributeTypeFacets.filter(
                    o => !unfilteredAttributeTypeFacets.some(p => p.attributeTypeId === o.attributeTypeId),
                ),
            );
        };

        return {
            loaded: true,
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: mergeAttributeTypeFacets(
                unfilteredProductCollection?.attributeTypeFacets,
                filteredProductCollection?.attributeTypeFacets,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
            isAuthenticated,
            ShowSelectedFilter,
            isMultipleFilterSelected,
            ButtonClicked,
            location,
            pageType,
            productListFilterDetails,
        };
    }
    return {
        loaded: false,
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
        isAuthenticated: false,
        ShowSelectedFilter: false,
        isMultipleFilterSelected: true,
        ButtonClicked: false,
        location: [] as any,
        pageType: [] as any,
        productListFilterDetails: [] as any,
    };
};

const mapDispatchToProps = {
    removeProductFilters,
    clearAllProductFilters,
    setFilterQuery,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListFilterListStyles {
    wrapper?: InjectableCss;
    skipFiltersButtonWrapper?: InjectableCss;
    skipFiltersButton?: SkipNavStyles;
    filterText?: TypographyPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
}

export const filterListStyles: ProductListFilterListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    filterText: {
        variant: "h3",
    },
    skipFiltersButtonWrapper: {
        css: css`
            position: relative;
            top: -65px;
            width: 100%;
        `,
    },
    clearAllLink: {
        css: css`
            padding-bottom: 20px;
        `,
    },
};

const styles = filterListStyles;

const ProductListFilterList: FC<Props> = ({
    loaded,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    categoryFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    removeProductFilters,
    clearAllProductFilters,
    currency,
    language,
    isAuthenticated,
    filterApplied,
    ShowSelectedFilter,
    isMultipleFilterSelected,
    ButtonClicked,
    setFilterQuery,
    location,
    pageType,
    productListFilterDetails,
}) => {
    if (!loaded) {
        return null;
    }

    const [customSelectedBrandID, setCustomSelectedBrandID] = useState(selectedBrandIds);
    const [customSelectedCategoryID, setCustomSelectedCategoryId] = useState(selectedCategoryId);
    const [customSelectedAttributeValuesIds, setCustomSelectedAttributeValuesIds] =
        useState(selectedAttributeValuesIds);
    const [customPreviouslyPurchasedProducts, setCustomPreviouslyPurchasedProducts] =
        useState(previouslyPurchasedProducts);
    const [customStockedItemsOnly, setCustomStockedItemsOnly] = useState(stockedItemsOnly);
    const [customSelectedProductLineIds, setCustomSelectedProductLineIds] = useState(selectedProductLineIds);
    const [customSearchWithinQueries, setCustomSearchWithinQueries] = useState(searchWithinQueries);
    const [customSelectedPriceFilters, setCustomSelectedPriceFilters] = useState(selectedPriceFilters);
    const [initialLoad, setInitialLoad] = useState(true);

    useEffect(() => {
        const parseQueryString = (queryString: any) => {
            const params: any = {};
            const pairs = queryString.startsWith("?") ? queryString.substring(1).split("&") : [];
            for (const pair of pairs) {
                const [key, value] = pair.split("=");
                if (key) {
                    params[key] = value;
                }
            }
            return params;
        };

        const searchParams = parseQueryString(location.search);
        const hasOnlyPageSize = "pageSize" in searchParams && Object.keys(searchParams).length === 1;

        if (location.search === "" || hasOnlyPageSize) {
            dispatch({
                type: "PLP/ShowSelectedFilter",
                ShowSelectedFilter: false,
            });
        } else if (location.search !== "" && location.pathname !== "/Search") {
            dispatch({
                type: "PLP/ShowSelectedFilter",
                ShowSelectedFilter: true,
            });
        }
    }, [location.search]);

    useEffect(() => {
        if (
            initialLoad &&
            (selectedBrandIds ||
                selectedAttributeValuesIds ||
                selectedCategoryId ||
                previouslyPurchasedProducts ||
                stockedItemsOnly ||
                selectedProductLineIds ||
                searchWithinQueries ||
                selectedPriceFilters)
        ) {
            setCustomSelectedBrandID(selectedBrandIds);
            setCustomSelectedAttributeValuesIds(selectedAttributeValuesIds);
            setCustomSelectedCategoryId(selectedCategoryId);
            setCustomPreviouslyPurchasedProducts(previouslyPurchasedProducts);
            setCustomStockedItemsOnly(stockedItemsOnly);
            setCustomSelectedProductLineIds(selectedProductLineIds);
            setCustomSearchWithinQueries(searchWithinQueries);
            setCustomSelectedPriceFilters(selectedPriceFilters);

            setInitialLoad(false);
        }
    }, [
        initialLoad,
        selectedBrandIds,
        selectedAttributeValuesIds,
        selectedCategoryId,
        previouslyPurchasedProducts,
        stockedItemsOnly,
        selectedProductLineIds,
        searchWithinQueries,
        selectedPriceFilters,
    ]);

    useEffect(() => {
        if (ButtonClicked) {
            dispatch({
                type: "PLP/ShowSelectedFilter",
                ShowSelectedFilter: true,
            });
            setCustomSelectedBrandID(selectedBrandIds);
            setCustomSelectedCategoryId(selectedCategoryId);
            setCustomSelectedAttributeValuesIds(selectedAttributeValuesIds);
            setCustomPreviouslyPurchasedProducts(previouslyPurchasedProducts);
            setCustomStockedItemsOnly(stockedItemsOnly);
            setCustomSelectedProductLineIds(selectedProductLineIds);
            setCustomSearchWithinQueries(searchWithinQueries);
            setCustomSelectedPriceFilters(selectedPriceFilters);
        }
    }, [ButtonClicked]);

    useEffect(() => {
        if (location.search === "") {
            dispatch({
                type: "PLP/ShowSelectedFilter",
                ShowSelectedFilter: false,
            });
        }
    }, []);

    useEffect(() => {
        if (location.pathname) {
            attributeTypeFacetsArray = [];
            attributeValueFacetsArray = [];
            setCustomSelectedAttributeValuesIds([]);
        }
    }, [location.pathname]);

    const clickRemoveStockedItemsOnly = () => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ stockedItemsOnly: false });
    };
    const clickRemoveStockedItemsOnlyCustom = () => {
        removeProductFilters({ stockedItemsOnly: false });
    };

    const clickRemovePreviouslyPurchasedProducts = () => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ previouslyPurchasedProducts: false });
    };
    const clickRemovePreviouslyPurchasedProductsCustom = () => {
        removeProductFilters({ previouslyPurchasedProducts: false });
    };

    const clickRemoveSearchHandler = (search: string) => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ searchWithinQueries: [search] });
    };
    const clickRemoveSearchHandlerCustom = (search: string) => {
        removeProductFilters({ searchWithinQueries: [search] });
    };

    const clickRemoveBrandHandler = (customSelectedBrandID: string) => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ brandIds: [customSelectedBrandID] });
    };
    const clickRemoveBrandHandlerCustom = (id: string) => {
        removeProductFilters({ brandIds: [id] });
    };

    const clickRemoveProductLineHandler = (id: string) => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ productLineIds: [id] });
    };
    const clickRemoveProductLineHandlerCustom = (id: string) => {
        removeProductFilters({ productLineIds: [id] });
    };

    const clickRemovePriceHandler = (price: string) => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ priceFilters: [price] });
    };
    const clickRemovePriceHandlerCustom = (price: string) => {
        removeProductFilters({ priceFilters: [price] });
    };

    const clickRemoveAttributeValueHandler = (id: string) => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ attributeValueIds: [id] });
    };
    const clickRemoveAttributeValueHandlerCustom = (id: string) => {
        removeProductFilters({ attributeValueIds: [id] });
    };

    const clickRemoveCategoryHandler = () => {
        setFilterQuery({
            isApplyFilter: true,
        } as any);

        dispatch({
            type: "PLP/ButtonClicked",
            buttonClicked: true,
        });

        removeProductFilters({ categoryId: selectedCategoryId });
    };
    const clickRemoveCategoryHandlerCustom = () => {
        removeProductFilters({ categoryId: selectedCategoryId });
    };

    const dispatch = useDispatch();

    const clearAll = async () => {
        await clearAllProductFilters();
        dispatch({
            type: "PLP/ShowSelectedFilter",
            ShowSelectedFilter: false,
        });
    };

    const brandNameFacets: any = [];
    let attributeTypeFacetsArray: any = [];
    let attributeValueFacetsArray: any = [];

    const anyActiveFilters =
        customSearchWithinQueries?.length ||
        customStockedItemsOnly ||
        customPreviouslyPurchasedProducts ||
        (brandFacets && customSelectedBrandID?.length) ||
        (productLineFacets && customSelectedProductLineIds?.length) ||
        (priceFacets && customSelectedPriceFilters?.length) ||
        (attributeTypeFacets &&
            customSelectedAttributeValuesIds?.length &&
            attributeTypeFacetsArray &&
            attributeValueFacetsArray) ||
        customSelectedCategoryID;

    const anyMultipleActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId;

    return (
        <>
            {ShowSelectedFilter && isMultipleFilterSelected && (
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                        <ProductListPageDataContext.Consumer>
                            {({ ref }) => {
                                return ref ? (
                                    <SkipNav
                                        text={translate("Skip to Results")}
                                        extendedStyles={styles.skipFiltersButton}
                                        destination={ref}
                                    />
                                ) : undefined;
                            }}
                        </ProductListPageDataContext.Consumer>
                    </StyledWrapper>
                    {customPreviouslyPurchasedProducts && (
                        <Tag
                            {...styles.filterTag}
                            onDelete={clickRemovePreviouslyPurchasedProducts}
                            data-test-selector="previouslyPurchasedProductsFilter"
                        >
                            {translate("Previously Purchased Only")}
                        </Tag>
                    )}
                    {customStockedItemsOnly && isAuthenticated && (
                        <Tag
                            {...styles.filterTag}
                            onDelete={clickRemoveStockedItemsOnly}
                            data-test-selector="stockedItemsOnlyFilter"
                        >
                            {translate("Stocked Items Only")}
                        </Tag>
                    )}
                    {customSearchWithinQueries?.map(s => (
                        <Tag
                            key={s}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveSearchHandler(s)}
                            data-test-selector="searchWithinFilter"
                        >
                            {translate("Search")}: {s}
                        </Tag>
                    ))}
                    {brandFacets &&
                        customSelectedBrandID?.map(id => {
                            const brandNameFacet = brandFacets?.find(o => o.id === id)?.name;
                            if (brandNameFacet) {
                                brandNameFacets.push(brandNameFacet);
                            }

                            return (
                                <>
                                    {brandNameFacet && (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemoveBrandHandler(id)}
                                            data-test-selector={`brandFilter${id}`}
                                        >
                                            {translate("Brand")}: {brandNameFacet}
                                        </Tag>
                                    )}
                                </>
                            );
                        })}
                    {productLineFacets &&
                        customSelectedProductLineIds?.map(id => (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemoveProductLineHandler(id)}
                                data-test-selector={`productLineFilter${id}`}
                            >
                                {translate("Product Line")}: {productLineFacets?.find(o => o.id === id)?.name}
                            </Tag>
                        ))}
                    {customSelectedCategoryID && (
                        <Tag
                            {...styles.filterTag}
                            onDelete={() => clickRemoveCategoryHandler()}
                            data-test-selector={`categoryFilter${selectedCategoryId}`}
                        >
                            {translate("Category")}:{" "}
                            {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription}
                        </Tag>
                    )}
                    {priceFacets &&
                        customSelectedPriceFilters?.map(id => (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemovePriceHandler(id)}
                                data-test-selector={`priceFilter${id}`}
                            >
                                {translate("Price")}:{" "}
                                {formatPriceRangeFacet(
                                    priceFacets?.find(o => o.minimumPrice.toString() === id),
                                    currency,
                                    language,
                                )}
                            </Tag>
                        ))}
                    {attributeTypeFacets &&
                        customSelectedAttributeValuesIds?.map(id => {
                            const attributeTypeFacet = attributeTypeFacets?.find(at =>
                                at.attributeValueFacets?.find(av => av.attributeValueId === id),
                            );
                            const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                                av => av.attributeValueId === id,
                            );

                            if (attributeTypeFacet && attributeValueFacet) {
                                attributeTypeFacetsArray.push(attributeTypeFacet);
                                attributeValueFacetsArray.push(attributeValueFacet);
                            }

                            return (
                                <>
                                    {attributeTypeFacet && attributeValueFacet && (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemoveAttributeValueHandler(id)}
                                            data-test-selector={`attributeValueFilter${id}`}
                                        >
                                            {attributeTypeFacet?.nameDisplay}: {attributeValueFacet?.valueDisplay}
                                        </Tag>
                                    )}
                                </>
                            );
                        })}
                    {anyActiveFilters && (
                        <Link
                            {...styles.clearAllLink}
                            onClick={() => clearAll()}
                            data-test-selector="productListClearAll"
                        >
                            {translate("Clear All")}
                        </Link>
                    )}
                </StyledWrapper>
            )}
            {!isMultipleFilterSelected && (
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                        <ProductListPageDataContext.Consumer>
                            {({ ref }) => {
                                return ref ? (
                                    <SkipNav
                                        text={translate("Skip to Results")}
                                        extendedStyles={styles.skipFiltersButton}
                                        destination={ref}
                                    />
                                ) : undefined;
                            }}
                        </ProductListPageDataContext.Consumer>
                    </StyledWrapper>
                    {previouslyPurchasedProducts && (
                        <Tag
                            {...styles.filterTag}
                            onDelete={clickRemovePreviouslyPurchasedProductsCustom}
                            data-test-selector="previouslyPurchasedProductsFilter"
                        >
                            {translate("Previously Purchased Only")}
                        </Tag>
                    )}
                    {stockedItemsOnly && isAuthenticated && (
                        <Tag
                            {...styles.filterTag}
                            onDelete={clickRemoveStockedItemsOnlyCustom}
                            data-test-selector="stockedItemsOnlyFilter"
                        >
                            {translate("Stocked Items Only")}
                        </Tag>
                    )}
                    {searchWithinQueries?.map(s => (
                        <Tag
                            key={s}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveSearchHandlerCustom(s)}
                            data-test-selector="searchWithinFilter"
                        >
                            {translate("Search")}: {s}
                        </Tag>
                    ))}
                    {brandFacets &&
                        selectedBrandIds?.map(id => (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemoveBrandHandlerCustom(id)}
                                data-test-selector={`brandFilter${id}`}
                            >
                                {translate("Brand")}: {brandFacets?.find(o => o.id === id)?.name}
                            </Tag>
                        ))}
                    {productLineFacets &&
                        selectedProductLineIds?.map(id => (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemoveProductLineHandlerCustom(id)}
                                data-test-selector={`productLineFilter${id}`}
                            >
                                {translate("Product Line")}: {productLineFacets?.find(o => o.id === id)?.name}
                            </Tag>
                        ))}
                    {selectedCategoryId && (
                        <Tag
                            {...styles.filterTag}
                            onDelete={() => clickRemoveCategoryHandlerCustom()}
                            data-test-selector={`categoryFilter${selectedCategoryId}`}
                        >
                            {translate("Category")}:{" "}
                            {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription}
                        </Tag>
                    )}
                    {priceFacets &&
                        selectedPriceFilters?.map(id => (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemovePriceHandlerCustom(id)}
                                data-test-selector={`priceFilter${id}`}
                            >
                                {translate("Price")}:{" "}
                                {formatPriceRangeFacet(
                                    priceFacets?.find(o => o.minimumPrice.toString() === id),
                                    currency,
                                    language,
                                )}
                            </Tag>
                        ))}
                    {attributeTypeFacets &&
                        selectedAttributeValuesIds?.map(id => {
                            const attributeTypeFacet = attributeTypeFacets?.find(at =>
                                at.attributeValueFacets?.find(av => av.attributeValueId === id),
                            );
                            const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                                av => av.attributeValueId === id,
                            );

                            return (
                                <Tag
                                    key={id}
                                    {...styles.filterTag}
                                    onDelete={() => clickRemoveAttributeValueHandlerCustom(id)}
                                    data-test-selector={`attributeValueFilter${id}`}
                                >
                                    {attributeTypeFacet?.nameDisplay}: {attributeValueFacet?.valueDisplay}
                                </Tag>
                            );
                        })}
                    {anyMultipleActiveFilters && (
                        <Link
                            {...styles.clearAllLink}
                            onClick={() => clearAllProductFilters()}
                            data-test-selector="productListClearAll"
                        >
                            {translate("Clear All")}
                        </Link>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListFilterList),
    definition: {
        group: "Product List",
        displayName: "Filter List",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
